import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useCallback, useState } from 'react'
import { useToken } from '../../utils/context/TokenContext'
import CustomForm from './CustomForm'
import { requestInformation } from '../../services/form-api'

const Root = styled(Box)(({ theme }) => ({
  margin: '16px auto',
  maxWidth: '500px',
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(4),
}))

const ErrorContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(4),
  color: theme.palette.error.main,
}))

const fieldStyles = {
  '& input': {
    width: '100%',
    height: '40px',
    marginBottom: '16px',
    borderRadius: '16px',
  },
}

const buttonStyles = {
  width: '100% !important',
}

const formFields = [
  {
    type: 'input',
    props: {
      type: 'email',
      id: 'email',
      name: 'email',
      required: true,
      label: 'Email:',
      autoComplete: 'email',
      freevalidation: 'false',
    },
  },
]

const formCopyStyles = { marginBottom: 2 }

const EmailForm = React.memo(({ emailId }) => {
  const { csrfToken, fetchCsrfToken, csrfLoading, csrfError, userEmail } = useToken()
  const [emailSent, setEmailSent] = useState(false)
  const [loading, setLoading] = useState(true)
  const initialValues = React.useMemo(() => ({ email: userEmail || '' }), [userEmail])
  let formCopy
  if (emailId === 0) {
    formCopy =
      "Our standard models are launching soon. Share your email below to be notified as soon as they're available!"
  } else if (emailId === 1) {
    formCopy =
      "Ready to learn more? Share your email below, and our team will reach out to get started."
  } else if (emailId === 2) {
    formCopy =
      "Let's get started! Share your email below, and our team will reach out for more information."
  }

  const handleSubmit = useCallback(
    async (formData) => {
      const { email } = formData
      let emailIdToUse = emailId.toString()
      try {
        let newToken 
        if (!csrfToken) {
          newToken = await fetchCsrfToken()
        }
        setLoading(true)
        await requestInformation(email, emailIdToUse, csrfToken || newToken)
        setEmailSent(true)
      } catch (err) {
        console.error(err.message || 'Login failed. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [emailId]
  )

  useEffect(() => {
    if (!csrfToken && !csrfLoading && !csrfError) {
      fetchCsrfToken().catch((error) => {
        console.error('Failed to fetch CSRF token:', error)
      })
    }
  }, [csrfToken, csrfLoading, csrfError, fetchCsrfToken])

  if (csrfLoading) {
    return (
      <Root>
        <LoadingContainer role="status" aria-live="polite">
          <Typography variant="body1">Loading...</Typography>
        </LoadingContainer>
      </Root>
    )
  }

  if (csrfError) {
    return (
      <Root>
        <ErrorContainer role="alert">
          <Typography variant="body1">
            Failed to load form. Please try again later.
          </Typography>
        </ErrorContainer>
      </Root>
    )
  }

  return (
    <Root>
      <Typography variant="h4" sx={formCopyStyles}>
        {formCopy}
      </Typography>
      <CustomForm
        formId="email-form"
        emailId={emailId}
        isModal={true}
        ctaText="Submit"
        formFields={formFields}
        onSubmit={handleSubmit}
        formCSRF={csrfToken}
        formStyles={fieldStyles}
        buttonStyles={buttonStyles}
        initialValues={initialValues}
      />
    </Root>
  )
})

EmailForm.displayName = 'EmailForm'

EmailForm.propTypes = {
  emailId: PropTypes.number.isRequired,
}

export default EmailForm
